/** Rechnungen list — tabs (Alle/Anzahlungen/Schlussrechnungen/Bezahlt/Offen)
 *  + tabulka + PDF preview modal. Pixel-perfect z briefu §10. */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Ic } from "@/components/ui/icons";
import { Empty } from "@/components/ui/empty";
import { Modal } from "@/components/ui/modal";
import { StatusBadge } from "@/components/ui/badge";
import { fetchAllInvoices, type InvoicesFilter } from "@/lib/fetchers";
import { authedFetch } from "@/lib/auth";
import { EDGE_FUNCTION_BASE } from "@/lib/supabase";
import { fmtEur, fmtEurCents } from "@/lib/format";
import { invoiceStatusMeta } from "@/lib/status";
import { queryKeys } from "@/lib/queries";
import { t } from "@/i18n/de";
import type { PartnerInvoice } from "@/lib/types";

type TabId = "all" | "advance" | "final" | "paid" | "open";

const TABS: { id: TabId; label: string; filter: InvoicesFilter }[] = [
  { id: "all", label: "Alle", filter: {} },
  { id: "advance", label: "Anzahlungen", filter: { invoiceType: "advance" } },
  { id: "final", label: "Schlussrechnungen", filter: { invoiceType: "final" } },
  { id: "paid", label: "Bezahlt", filter: { paidOnly: true } },
  { id: "open", label: "Offen", filter: { openOnly: true } },
];

interface PdfLinkResponse {
  url: string;
  mime_type: string;
  file_name: string;
}

export function InvoicesRoute() {
  const [tab, setTab] = useState<TabId>("all");
  const tabFilter = useMemo(() => TABS.find((x) => x.id === tab)?.filter ?? {}, [tab]);

  const invoicesQ = useQuery({
    queryKey: queryKeys.invoicesAll(tabFilter as Record<string, unknown>),
    queryFn: () => fetchAllInvoices(tabFilter),
  });

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState("");
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const openPdf = async (inv: PartnerInvoice) => {
    if (!inv.pdf_document_id) {
      setError("PDF noch nicht verfügbar.");
      return;
    }
    setOpening(inv.id);
    setError(null);
    try {
      const resp = await authedFetch<PdfLinkResponse>(
        `${EDGE_FUNCTION_BASE}/partner-portal/pdf-link`,
        { method: "POST", body: JSON.stringify({ document_id: inv.pdf_document_id }) },
      );
      setPreviewUrl(resp.url);
      setPreviewName(resp.file_name || `${inv.invoice_number}.pdf`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setOpening(null);
    }
  };

  const invoices = invoicesQ.data || [];

  return (
    <div style={{ padding: "32px 36px", maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 className="display" style={{ margin: 0, fontSize: 28 }}>
          {t.nav.invoices}
        </h1>
      </div>

      <div
        style={{
          display: "flex",
          gap: 4,
          borderBottom: "1px solid var(--border)",
          marginBottom: 20,
          overflowX: "auto",
        }}
      >
        {TABS.map((tabItem) => (
          <button
            key={tabItem.id}
            type="button"
            onClick={() => setTab(tabItem.id)}
            style={{
              padding: "12px 14px",
              background: "transparent",
              border: 0,
              color: tab === tabItem.id ? "var(--text)" : "var(--text-2)",
              fontSize: 13.5,
              fontWeight: 500,
              position: "relative",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {tabItem.label}
            {tab === tabItem.id && (
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: -1,
                  height: 2,
                  background: "var(--gold)",
                }}
              />
            )}
          </button>
        ))}
      </div>

      {error && (
        <div
          role="alert"
          style={{
            marginBottom: 14,
            padding: 10,
            fontSize: 12.5,
            color: "var(--red)",
            background: "var(--red-12)",
            border: "1px solid rgba(248,113,113,0.22)",
            borderRadius: "var(--r-md)",
          }}
        >
          {error}
        </div>
      )}

      {invoicesQ.isLoading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>{t.loading}</div>
      ) : invoices.length === 0 ? (
        <Empty title="Keine Rechnungen in dieser Ansicht." />
      ) : (
        <div className="card" style={{ overflow: "hidden" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Nr</th>
                <th>Art</th>
                <th>Datum</th>
                <th>Fällig</th>
                <th style={{ textAlign: "right" }}>Betrag</th>
                <th>Status</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="row-clickable" onClick={() => void openPdf(inv)}>
                  <td className="num" style={{ fontWeight: 500 }}>
                    {inv.invoice_number}
                  </td>
                  <td style={{ color: "var(--text-2)", fontSize: 12.5 }}>{inv.kind_label}</td>
                  <td className="num" style={{ color: "var(--text-2)", fontSize: 12.5 }}>
                    {formatShort(inv.issued_at)}
                  </td>
                  <td className="num" style={{ color: "var(--text-2)", fontSize: 12.5 }}>
                    {formatShort(inv.due_at)}
                  </td>
                  <td className="right num" style={{ fontWeight: 500 }}>
                    {fmtEur(inv.net_amount)}
                  </td>
                  <td>
                    <StatusBadge meta={invoiceStatusMeta(inv.display_status)} />
                  </td>
                  <td style={{ textAlign: "right", color: "var(--text-3)" }}>
                    {opening === inv.id ? (
                      <Ic.Loader size={14} className="animate-spin" />
                    ) : (
                      <Ic.File size={14} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!previewUrl} onOpenChange={(o) => !o && setPreviewUrl(null)} width={1000}>
        {previewUrl && (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <div>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{previewName}</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-secondary btn-sm"
                  download
                >
                  <Ic.ArrowUpRight size={12} /> Herunterladen
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewUrl(null)}
                  className="btn btn-ghost btn-sm"
                  style={{ padding: 6 }}
                >
                  <Ic.Close size={16} />
                </button>
              </div>
            </div>
            <iframe
              src={previewUrl}
              title={previewName}
              style={{
                width: "100%",
                height: "75vh",
                border: "1px solid var(--border)",
                borderRadius: 8,
                background: "var(--bg-base)",
              }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

function formatShort(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear() % 100}`;
}

// Re-export pro hint, nepoužité přímo
void fmtEurCents;
